import { ColDef } from '@ag-grid-community/core';
import { Box } from '@atgfr8/atg-ui-tailwind';
import { dateComparator, dateFormatter } from '@store/loadboard/utils';
import { formatAmount } from '@utils/utils';

import OrderHistoryGrid from './OrderHistoryGrid';

const OrderHistory = () => {
  const commonFilterParams = {
    suppressAndOrCondition: true,
    filterOptions: ['startsWith', 'equals', 'notEqual'],
    buttons: ['apply', 'reset'],
  };

  const agGridCellStyle = {
    padding: '10px',
    'word-break': 'break-word',
    'white-space': 'normal',
    'line-height': '1.6 ',
    'align-items': 'center !important',
  };

  const filterOptionsList = [
    'lessThan',
    'greaterThan',
    'equals',
    'notEqual',
    'contains',
    'startsWith',
    'endsWith',
  ];

  const orderHistoryColumns: ColDef[] = [
    {
      field: 'loadNumber',
      headerName: 'Load #',
      minWidth: 105,
      filter: 'agTextColumnFilter',
      filterParams: {
        suppressAndOrCondition: true,
        buttons: ['apply', 'reset'],
        filterOptions: filterOptionsList,
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      minWidth: 80,
    },
    {
      field: 'originCity',
      headerName: 'Origin City',
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      minWidth: 150,
    },
    {
      field: 'originState',
      headerName: 'Origin State',
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      minWidth: 80,
    },
    {
      field: 'pickUp',
      headerName: 'PickDate',
      filter: 'agDateColumnFilter',
      valueFormatter: dateFormatter,
      filterParams: {
        comparator: dateComparator,
        buttons: ['reset'],
        suppressAndOrCondition: true,
        filterOptions: ['lessThan', 'greaterThan', 'inRange', 'equals'],
        minValidYear: 1900,
        maxValidYear: new Date().getFullYear() + 1,
      },
      minWidth: 100,
    },
    {
      field: 'destinationCity',
      headerName: 'DestinationCity',
      filter: 'agTextColumnFilter',
      autoHeight: true,
      filterParams: commonFilterParams,
      minWidth: 120,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'destinationState',
      headerName: 'Dest State',
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      minWidth: 80,
    },
    {
      field: 'dropoff',
      headerName: 'DropDate',
      filter: 'agDateColumnFilter',
      valueFormatter: dateFormatter,
      sort: 'desc',
      filterParams: {
        comparator: dateComparator,
        buttons: ['reset'],
        suppressAndOrCondition: true,
        filterOptions: ['lessThan', 'greaterThan', 'inRange', 'equals'],
        minValidYear: 1900,
        maxValidYear: new Date().getFullYear() + 1,
      },
      minWidth: 100,
    },
    {
      field: 'referenceNumber',
      headerName: 'Ref #',
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      minWidth: 100,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'proNumber',
      headerName: 'Pro #',
      filter: 'agTextColumnFilter',
      autoHeight: true,
      filterParams: commonFilterParams,
      minWidth: 80,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'trackingNumber',
      headerName: 'Tracking #',
      filter: 'agTextColumnFilter',
      autoHeight: true,
      filterParams: commonFilterParams,
      minWidth: 100,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'stopPickupNumber',
      headerName: 'Stop: Pickup #',
      filter: 'agTextColumnFilter',
      autoHeight: true,
      filterParams: commonFilterParams,
      minWidth: 100,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'stopDropOffNumber',
      headerName: 'Stop: Dropoff #',
      filter: 'agTextColumnFilter',
      autoHeight: true,
      filterParams: commonFilterParams,
      minWidth: 100,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'numLoads',
      headerName: 'Loads',
      filter: 'agTextColumnFilter',
      filterParams: {
        suppressAndOrCondition: true,
        buttons: ['apply', 'reset'],
        filterOptions: filterOptionsList,
      },
      minWidth: 80,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'loadProduct',
      headerName: 'Load Product',
      filter: 'agTextColumnFilter',
      autoHeight: true,
      minWidth: 120,
      cellStyle: agGridCellStyle,
      filterParams: commonFilterParams,
    },
    {
      field: 'equipment',
      headerName: 'Equipment',
      filter: 'agTextColumnFilter',
      autoHeight: true,
      filterParams: commonFilterParams,
      minWidth: 120,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'carrierName',
      headerName: 'Carrier Name',
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      minWidth: 150,
      autoHeight: true,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'agent',
      headerName: 'Agent',
      filter: 'agTextColumnFilter',
      autoHeight: true,
      filterParams: commonFilterParams,
      minWidth: 100,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'revenue',
      headerName: 'Revenue',
      filter: 'agNumberColumnFilter',
      autoHeight: true,
      minWidth: 120,
      filterParams: {
        suppressAndOrCondition: true,
        buttons: ['apply', 'reset'],
        filterOptions: filterOptionsList,
      },
      valueFormatter: (params) => {
        return formatAmount(params.value as number, {}, '-', true);
      },
      cellStyle: agGridCellStyle,
    },
    {
      field: 'cost',
      headerName: 'Cost',
      filter: 'agNumberColumnFilter',
      autoHeight: true,
      minWidth: 120,
      filterParams: {
        suppressAndOrCondition: true,
        buttons: ['apply', 'reset'],
        filterOptions: filterOptionsList,
      },
      valueFormatter: (params) => {
        return formatAmount(params.value as number, {}, '-', true);
      },
      cellStyle: agGridCellStyle,
    },
    {
      field: 'profit',
      headerName: 'Profit',
      filter: 'agNumberColumnFilter',
      autoHeight: true,
      minWidth: 120,
      filterParams: {
        suppressAndOrCondition: true,
        buttons: ['apply', 'reset'],
        filterOptions: filterOptionsList,
      },
      valueFormatter: (params) => {
        return formatAmount(params.value as number, {}, '-', true);
      },
      cellStyle: agGridCellStyle,
    },
  ];

  return (
    <Box class="min-h-[400px] h-[calc(100vh-210px)]" backgroundColor="#E1ECF2">
      <OrderHistoryGrid orderHistoryColumns={orderHistoryColumns} />
    </Box>
  );
};

export default OrderHistory;
